import { gql } from '@apollo/client';

export const GetFarmDataQuery = gql`
  query ExportFactory_GetFarmData($farmIDs: [uuid!]!) {
    farm(where: { id: { _in: $farmIDs } }, order_by: { name: asc }) {
      id
      name
      feed_lines(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        name
        id
      }
      rooms(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
      }
    }
  }
`;

export const GetFeedEventCountQuery = gql`
  query ExportFactory_GetFeedEventCount($feedLineIDs: [uuid!]!, $startedAt: bigint!, $endedAt: bigint!) {
    feed_frame_aggregate(
      where: {
        feed_line_id: { _in: $feedLineIDs }
        started_at: { _gte: $startedAt }
        ended_at: { _lte: $endedAt }
        deleted_at: { _is_null: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetFaultCountQuery = gql`
  query ExportFactory_GetFaultCount(
    $feedLineIDs: [uuid!]!
    $startedAt: bigint!
    $endedAt: bigint!
    $faultCodes: [Int!]!
  ) {
    fault_aggregate(
      where: {
        device: { device_assignments: { feed_line_id: { _in: $feedLineIDs } } }
        started_at: { _lte: $endedAt }
        _or: [{ ended_at: { _gte: $startedAt } }, { ended_at: { _is_null: true } }]
        deleted_at: { _is_null: true }
        code: { _in: $faultCodes }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GetFeedFrameExportQuery = gql`
  query ExportFactory_GetFeedFrameExport(
    $feedLineID: uuid!
    $startedAt: bigint!
    $endedAt: bigint!
    $limit: Int!
    $offset: Int!
    $feedFrameAnalysisWhere: feed_frame_analysis_bool_exp
  ) {
    feed_frame(
      where: {
        feed_line_id: { _eq: $feedLineID }
        started_at: { _gte: $startedAt }
        ended_at: { _lte: $endedAt }
        deleted_at: { _is_null: true }
      }
      order_by: [{ started_at: asc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      id
      ended_at
      started_at
      feed_frame_analyses(where: $feedFrameAnalysisWhere, order_by: { created_at: desc_nulls_last }, limit: 1) {
        latest_estimated_mass_moved_in_grams
      }
    }
  }
`;

export const GetFaultCodeExportQuery = gql`
  query ExportFactory_FaultCodeExport(
    $feedLineID: uuid!
    $startedAt: bigint!
    $endedAt: bigint!
    $faultCodes: [Int!]!
    $limit: Int!
    $offset: Int!
    $faultFilter: fault_bool_exp!
    $algorithmVersionOverride: String
    $hasAlgorithmOverride: Boolean!
  ) {
    fault(
      where: {
        device: { device_assignments: { feed_line_id: { _eq: $feedLineID } } }
        started_at: { _lte: $endedAt }
        _or: [{ ended_at: { _gte: $startedAt } }, { ended_at: { _is_null: true } }]
        deleted_at: { _is_null: true }
        code: { _in: $faultCodes }
        _and: [$faultFilter]
      }
      order_by: [{ started_at: asc }, { id: asc }]
      limit: $limit
      offset: $offset
    ) {
      code
      ended_at
      started_at
      duration
      fault_comments(
        where: { _and: [{ deleted_at: { _is_null: true } }] }
        order_by: { created_at: desc_nulls_last }
        limit: 1
      ) {
        comment
        fault_root_cause {
          name
          reference_id
        }
      }
      animal_groups {
        external_id
      }
      # Use computed field when no override (handles all the DB logic)
      nofeed_seconds @skip(if: $hasAlgorithmOverride)
      # When override is set, query the nofeed_times directly and compute client-side
      fault_nofeed_times(
        where: { algorithm_version: { _eq: $algorithmVersionOverride } }
        limit: 1
        order_by: { created_at: desc }
      ) @include(if: $hasAlgorithmOverride) {
        nofeed_time_in_seconds
      }
    }
  }
`;

export const GetDailyConsumptionQuery = gql`
  query ExportFactory_GetDailyConsumption(
    $barnIds: [uuid!]!
    $start: bigint!
    $end: bigint!
    $timeZone: String!
    $alg: String
  ) {
    farm(where: { id: { _in: $barnIds } }, order_by: { name: asc }) {
      id
      name
      per_animal_feed_summaries(
        args: { p_after: $start, p_before: $end, p_interval: "day", p_time_zone: $timeZone, p_alg: $alg }
      ) {
        start_timestamp
        end_timestamp
        total_consumption
        per_animal_consumption
      }
      rooms(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
        per_animal_feed_summaries(
          args: { p_after: $start, p_before: $end, p_interval: "day", p_time_zone: $timeZone, p_alg: $alg }
        ) {
          start_timestamp
          end_timestamp
          total_consumption
          per_animal_consumption
        }
      }
      feed_lines(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
        feed_frame_summaries: feed_frame_summaries_v2(
          args: { p_after: $start, p_before: $end, p_interval: "day", p_time_zone: $timeZone, p_alg: $alg }
        ) {
          start_timestamp
          end_timestamp
          total_consumption
        }
      }
    }
  }
`;
